import { useState, useEffect, useCallback } from 'react';
import { notificationApi } from '../services/api';
import { Notification, NotificationListResponse } from '../types';
import { extractApiError } from '../utils/errorUtils';

/**
 * Notifications Hook for React Components (Issue #496)
 *
 * Provides access to user notifications with real-time polling.
 * Fetches notifications on mount and polls every 30 seconds for updates.
 *
 * @returns Object with notifications, unreadCount, loading, error, and helper methods
 *
 * @example
 * ```tsx
 * function NotificationCenter() {
 *   const { notifications, unreadCount, isLoading, markAsRead, markAllAsRead } = useNotifications();
 *
 *   if (isLoading) return <LoadingSpinner />;
 *
 *   return (
 *     <div>
 *       <h2>Notifications ({unreadCount})</h2>
 *       {notifications.map(n => (
 *         <NotificationItem key={n.id} notification={n} onRead={() => markAsRead(n.id)} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */

interface UseNotificationsResult {
  notifications: Notification[];
  unreadCount: number;
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  markAsRead: (notificationId: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export function useNotifications(): UseNotificationsResult {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch notifications from API (initial load or refresh)
  const fetchNotifications = useCallback(async (replace: boolean = true) => {
    try {
      setError(null);
      const response: NotificationListResponse = await notificationApi.getNotifications(20, 0);

      if (replace) {
        setNotifications(response.notifications);
      } else {
        // Shouldn't happen with replace=false in this function, but handle it
        setNotifications(prev => [...prev, ...response.notifications]);
      }

      setUnreadCount(response.unread_count);
      setTotalCount(response.total_count);
      setHasMore(response.has_more);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(new Error(apiError.message));
      console.error('Failed to fetch notifications:', apiError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Mark a notification as read (Issue #595 review fix: race condition)
  const markAsRead = useCallback(async (notificationId: number) => {
    try {
      // Check if notification was unread BEFORE the API call
      let wasUnread = false;
      setNotifications(prev => {
        const notification = prev.find(n => n.id === notificationId);
        if (notification && !notification.read) {
          wasUnread = true;
        }
        return prev; // Don't update yet
      });

      const updatedNotification = await notificationApi.markAsRead(notificationId);

      // Update local state using functional updates to avoid stale closure
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? updatedNotification : n)
      );

      // Decrement unread count only if notification was previously unread
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to mark notification as read:', apiError);
      throw err;
    }
  }, []); // Remove notifications dependency to avoid stale closure

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationApi.markAllAsRead();

      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true }))
      );
      setUnreadCount(0);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to mark all notifications as read:', apiError);
      throw err;
    }
  }, []);

  // Load more notifications (pagination)
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) {
      return; // Don't load if already loading or no more data
    }

    try {
      setIsLoadingMore(true);
      setError(null);

      const offset = notifications.length;
      const response: NotificationListResponse = await notificationApi.getNotifications(20, offset);

      // Append new notifications to existing list
      setNotifications(prev => [...prev, ...response.notifications]);
      setUnreadCount(response.unread_count);
      setTotalCount(response.total_count);
      setHasMore(response.has_more);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(new Error(apiError.message));
      console.error('Failed to load more notifications:', apiError);
    } finally {
      setIsLoadingMore(false);
    }
  }, [notifications.length, hasMore, isLoadingMore]);

  // Poll for new notifications without resetting pagination (Issue #595 review fix)
  const pollForUpdates = useCallback(async () => {
    try {
      // Only fetch metadata (unread count) without resetting loaded notifications
      const response: NotificationListResponse = await notificationApi.getNotifications(1, 0);

      // Update only metadata, don't reset notification list
      setUnreadCount(response.unread_count);
      setTotalCount(response.total_count);

      // If total count increased, there might be new notifications
      // User can manually refresh to see them
    } catch (err: unknown) {
      // Silently fail polling to avoid disrupting user experience
      const apiError = extractApiError(err);
      console.debug('Polling failed:', apiError);
    }
  }, []);

  // Refresh notifications (exposed for manual refresh)
  const refresh = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  // Initial fetch and polling setup
  useEffect(() => {
    let isMounted = true;
    let pollInterval: NodeJS.Timeout | null = null;

    // Check if user is authenticated before polling (Issue #722 - Fix 401 polling errors)
    const isAuthenticated = !!localStorage.getItem('access_token');

    if (!isAuthenticated) {
      // User not authenticated, skip polling to avoid 401 errors
      setIsLoading(() => false);

      // Return cleanup function even for early return to ensure proper cleanup
      return () => {
        isMounted = false;
        if (pollInterval) {
          clearInterval(pollInterval);
        }
      };
    }

    // Initial fetch
    fetchNotifications();

    // Set up polling every 30 seconds (only updates metadata, doesn't reset list)
    pollInterval = setInterval(() => {
      if (isMounted) {
        pollForUpdates();
      }
    }, 30000);

    // Cleanup function
    return () => {
      isMounted = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [fetchNotifications, pollForUpdates]);

  return {
    notifications,
    unreadCount,
    totalCount,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    markAsRead,
    markAllAsRead,
    refresh,
    loadMore,
  };
}
